import { useState } from 'react';

interface LandingProps {
  onLogin: () => void;
}

export default function LandingPage({ onLogin }: LandingProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ============================================ */}
      {/* NAVIGATION BAR                              */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold">⚙</div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">SAI KRISHNA INDUSTRIES</h1>
                <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Castor Wheels & Conveyors</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#products" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Products</a>
              <a href="#about" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">About</a>
              <a href="#industries" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Industries</a>
              <a href="#why-us" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Why Us</a>
              <a href="#contact" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
              <button onClick={onLogin} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer">Portal Login →</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600 cursor-pointer">☰</button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-100 space-y-1 pt-2">
              <a href="#products" className="block px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">Products</a>
              <a href="#about" className="block px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">About</a>
              <a href="#industries" className="block px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">Industries</a>
              <a href="#why-us" className="block px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">Why Us</a>
              <a href="#contact" className="block px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">Contact</a>
              <button onClick={onLogin} className="w-full mt-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg cursor-pointer">Portal Login →</button>
            </div>
          )}
        </div>
      </nav>

      {/* ============================================ */}
      {/* HERO SECTION                                */}
      {/* ============================================ */}
      <section className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-blue-700">Trusted Since 1995</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                Precision Castor Wheels
                <span className="block text-blue-600 mt-1">for Every Industry</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                India's leading manufacturer of heavy-duty castor wheels, lattice conveyors, and material handling solutions. Serving 664+ verified corporate clients across Ludhiana, Gurgaon & Kolkata.
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={onLogin} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-colors cursor-pointer flex items-center gap-2">
                  Access Quotation Portal →
                </button>
                <a href="#products" className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-sm rounded-lg border border-gray-300 transition-colors flex items-center gap-2">
                  View Products
                </a>
              </div>
              <div className="grid grid-cols-4 gap-4 pt-6 border-t border-gray-200">
                <div>
                  <div className="text-2xl font-bold text-gray-900">664+</div>
                  <div className="text-xs text-gray-500">Corporate Clients</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">6</div>
                  <div className="text-xs text-gray-500">Operating Entities</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">153</div>
                  <div className="text-xs text-gray-500">Product Codes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">28+</div>
                  <div className="text-xs text-gray-500">Years Experience</div>
                </div>
              </div>
            </div>
            {/* Hero Visual */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="text-3xl mb-2">⚙</div>
                    <div className="text-sm font-bold text-gray-900">Swivel Castors</div>
                    <div className="text-xs text-gray-500 mt-1">50–2000 kg capacity</div>
                    <div className="text-xs text-blue-600 font-semibold mt-2">12 sizes available</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="text-3xl mb-2">🔩</div>
                    <div className="text-sm font-bold text-gray-900">Fixed Castors</div>
                    <div className="text-xs text-gray-500 mt-1">100–3000 kg capacity</div>
                    <div className="text-xs text-blue-600 font-semibold mt-2">Heavy duty range</div>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="text-3xl mb-2">🏗</div>
                    <div className="text-sm font-bold text-gray-900">Lattice Conveyors</div>
                    <div className="text-xs text-gray-500 mt-1">Custom built to order</div>
                    <div className="text-xs text-blue-600 font-semibold mt-2">Aluminium / PVC / MS</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="text-3xl mb-2">🛞</div>
                    <div className="text-sm font-bold text-gray-900">Industrial Wheels</div>
                    <div className="text-xs text-gray-500 mt-1">6 materials, 22+ sizes</div>
                    <div className="text-xs text-blue-600 font-semibold mt-2">80mm – 250mm range</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRODUCT CATEGORIES                          */}
      {/* ============================================ */}
      <section id="products" className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Complete Range of Castor Solutions</h2>
            <p className="text-gray-500 mt-2 max-w-2xl mx-auto text-sm">From light-duty office chair castors to heavy-duty industrial wheels — we manufacture for every application</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: '⚙', name: 'Swivel Castors', desc: '360° rotation castor wheels for smooth maneuverability. Available in MS, SS, and polypropylene brackets.', tags: ['MSFB', 'SSFB', 'PPFB', 'SSAB'], stats: '50–2000 kg' },
              { icon: '🔩', name: 'Fixed Castors', desc: 'Rigid fixed-direction wheels for stable load carrying. Ideal for trolleys and conveyor systems.', tags: ['FX', 'FXB', 'Heavy Duty'], stats: '100–3000 kg' },
              { icon: '🛞', name: 'Industrial Wheels', desc: 'Nylon, Polypropylene, Polyurethane, Cast Iron, and Rubber wheels for diverse industrial applications.', tags: ['Nylon', 'PU', 'Rubber', 'CI'], stats: '80–250mm' },
              { icon: '🏗', name: 'Lattice Conveyors', desc: 'Custom lattice conveyor systems with PVC lags, canvas belting, and spreader bars for material handling.', tags: ['PVC', 'Canvas', 'MS Frame'], stats: 'Custom sizes' },
              { icon: '⚙', name: 'Bearings & Components', desc: 'Ball bearings (6201–6206), circlips, thread guards, nut-bolts, bushes, and axle assemblies.', tags: ['BB', 'CIR', 'TG', 'NB'], stats: '10 types' },
              { icon: '📦', name: 'Accessories', desc: 'Flanges, adapters, foot brakes, spacers, and complete assembly accessories for castor systems.', tags: ['FC', 'Adapters', 'FB'], stats: 'Full range' },
            ].map((product, i) => (
              <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-xl">{product.icon}</div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{product.stats}</span>
                </div>
                <h3 className="font-bold text-gray-900">{product.name}</h3>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{product.desc}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {product.tags.map((tag, j) => (
                    <span key={j} className="text-[10px] font-semibold px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* INDUSTRIES WE SERVE                         */}
      {/* ============================================ */}
      <section id="industries" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Industries We Serve</h2>
            <p className="text-gray-500 mt-2 text-sm">Trusted across diverse sectors for reliable material handling solutions</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { icon: '🧵', name: 'Textile & Garment', count: '120+ clients' },
              { icon: '🏭', name: 'Automotive', count: '80+ clients' },
              { icon: '📦', name: 'Warehousing', count: '95+ clients' },
              { icon: '🛒', name: 'FMCG & Retail', count: '60+ clients' },
              { icon: '🏗', name: 'Construction', count: '45+ clients' },
              { icon: '🏥', name: 'Healthcare', count: '30+ clients' },
              { icon: '🔩', name: 'Engineering', count: '70+ clients' },
              { icon: '🌾', name: 'Agriculture', count: '40+ clients' },
              { icon: '📦', name: 'Logistics', count: '55+ clients' },
              { icon: '🏢', name: 'Office & IT', count: '35+ clients' },
              { icon: '🔧', name: 'Heavy Industry', count: '50+ clients' },
              { icon: '🍽', name: 'Food & Beverage', count: '25+ clients' },
            ].map((ind, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 text-center hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="text-2xl mb-2">{ind.icon}</div>
                <div className="text-xs font-bold text-gray-900">{ind.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{ind.count}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHY CHOOSE US                               */}
      {/* ============================================ */}
      <section id="why-us" className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Why Choose SKI</h2>
            <p className="text-gray-500 mt-2 text-sm">Trusted by 664+ corporate clients for quality, reliability, and service</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: '🏭', title: '3 Manufacturing Plants', desc: 'Ludhiana, Gurgaon & Kolkata — serving pan-India with local production and quick dispatch.' },
              { icon: '✅', title: 'ISO Certified Quality', desc: 'Every product tested to international standards. HSN: 87169090, 84483990, 84482000, 39269010.' },
              { icon: '⚡', title: '10-12 Day Delivery', desc: 'Fast turnaround from order confirmation to dispatch. Standard sizes ship within 7 days.' },
              { icon: '💰', title: 'Direct Manufacturer Pricing', desc: 'No middlemen, no markup. Factory-direct pricing with volume discounts for bulk orders.' },
              { icon: '🔧', title: 'Custom Engineering', desc: 'Custom sizes, materials, and configurations available. In-house design team for special requirements.' },
              { icon: '📋', title: 'VBA Pricing Engine', desc: 'Proprietary pricing calculator ensuring accurate, consistent quotations every time.' },
              { icon: '📞', title: 'Dedicated Support', desc: '7 sales representatives across 6 entities. Direct access to technical and sales support.' },
              { icon: '🌐', title: 'Pan-India Presence', desc: 'Offices in Punjab, Haryana, and West Bengal. Export-ready for international clients.' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-all">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm">{item.title}</h3>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ABOUT SECTION                               */}
      {/* ============================================ */}
      <section id="about" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-5">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">About Sai Krishna Industries</h2>
              <p className="text-gray-600 leading-relaxed text-sm">
                With over 28 years of expertise in castor wheel manufacturing, Sai Krishna Industries has grown from a single workshop in Ludhiana to a multi-branch operation serving clients across India.
              </p>
              <p className="text-gray-600 leading-relaxed text-sm">
                Our 3 operating entities — Sai Krishna Industries (SKI), Sutran Polymers Pvt. Ltd. (SPPL), and Sutran Conveyors Pvt. Ltd. (SCPL) — cover the complete range from raw material sourcing to finished product delivery.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-xl font-bold text-gray-900">4,400+</div>
                  <div className="text-xs text-gray-500">Transactions Processed</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-xl font-bold text-gray-900">153</div>
                  <div className="text-xs text-gray-500">Product Codes</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-xl font-bold text-gray-900">7</div>
                  <div className="text-xs text-gray-500">Sales Representatives</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-xl font-bold text-gray-900">3</div>
                  <div className="text-xs text-gray-500">Operating Entities</div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded">SKI</span>
                  <h4 className="font-bold text-gray-900 text-sm">Sai Krishna Industries</h4>
                </div>
                <p className="text-xs text-gray-500">Ludhiana, Punjab — Primary manufacturing & headquarters. Castor wheels, industrial wheels, bearings, components.</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 bg-emerald-600 text-white text-[10px] font-bold rounded">SPPL</span>
                  <h4 className="font-bold text-gray-900 text-sm">Sutran Polymers Pvt. Ltd.</h4>
                </div>
                <p className="text-xs text-gray-500">Ludhiana & Gurgaon — Polymer castors & industrial wheels. Injection molded precision components.</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded">SCPL</span>
                  <h4 className="font-bold text-gray-900 text-sm">Sutran Conveyors Pvt. Ltd.</h4>
                </div>
                <p className="text-xs text-gray-500">Ludhiana & Kolkata — Conveyors, lattices & material handling. Custom-built conveyor systems.</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 bg-orange-600 text-white text-[10px] font-bold rounded">TCI</span>
                  <h4 className="font-bold text-gray-900 text-sm">Techno Conveyor Industries</h4>
                </div>
                <p className="text-xs text-gray-500">Specialized conveyor solutions and material handling systems for industrial applications.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* TESTIMONIALS / CLIENTS                      */}
      {/* ============================================ */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Trusted By Leading Manufacturers</h2>
            <p className="text-gray-500 mt-2 text-sm">Our clients rely on us for consistent quality and timely delivery</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'A And A Spinners Ltd', location: 'Abohar, Punjab', quote: 'Reliable quality castor wheels for our textile machinery. Consistent supply and competitive pricing.', since: 'Since 2018' },
              { name: 'A.S.W.M. Spinning Mills', location: 'Ludhiana, Punjab', quote: 'Excellent lattice conveyor systems installed in our facility. Custom-built to our exact specifications.', since: 'Since 2015' },
              { name: 'Aay Bee International', location: 'Gurgaon, Haryana', quote: 'Trusted partner for all our material handling needs. Quick response and professional service.', since: 'Since 2020' },
              { name: 'Aarti International Limited', location: 'Ludhiana, Punjab', quote: 'Premium quality industrial wheels that have significantly reduced our maintenance costs.', since: 'Since 2019' },
              { name: 'Vardhman Textiles', location: 'Ludhiana, Punjab', quote: 'Long-standing relationship with SKI. Their VBA pricing engine ensures accurate quotations every time.', since: 'Since 2012' },
              { name: 'Nahar Industrial Enterprises', location: 'Ludhiana, Punjab', quote: 'Excellent range of bearings and components. One-stop shop for our castor assembly requirements.', since: 'Since 2016' },
            ].map((client, i) => (
              <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center text-sm font-bold text-blue-700">{client.name.charAt(0)}</div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{client.name}</div>
                    <div className="text-[10px] text-gray-500">{client.location} · {client.since}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 italic leading-relaxed">"{client.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* CONTACT SECTION                             */}
      {/* ============================================ */}
      <section id="contact" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Get in Touch</h2>
            <p className="text-gray-500 mt-2 text-sm">Request a quotation or discuss your requirements with our sales team</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
              <div className="text-2xl mb-2">📍</div>
              <h3 className="font-bold text-gray-900 text-sm">Head Office</h3>
              <p className="text-xs text-gray-500 mt-1.5">B-XXIX-536/35-B/A, Sua Road, Near Eastman Chowk, Industrial Area-C, Giaspura, Ludhiana - 141010 (Pb.)</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
              <div className="text-2xl mb-2">📞</div>
              <h3 className="font-bold text-gray-900 text-sm">Call Us</h3>
              <p className="text-xs text-gray-500 mt-1.5">0161-5034059<br/>93167-53530<br/>98887-53533</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
              <div className="text-2xl mb-2">📧</div>
              <h3 className="font-bold text-gray-900 text-sm">Email Us</h3>
              <p className="text-xs text-gray-500 mt-1.5">sutrantechnoconveyors@gmail.com<br/>For quotations & inquiries</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
              <div className="text-2xl mb-2">🏢</div>
              <h3 className="font-bold text-gray-900 text-sm">Branches</h3>
              <p className="text-xs text-gray-500 mt-1.5">Ludhiana (HQ)<br/>Gurgaon NCR<br/>Kolkata Depot</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <button onClick={onLogin} className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-colors cursor-pointer inline-flex items-center gap-2">
              Access Quotation Portal →
            </button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER                                      */}
      {/* ============================================ */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">⚙</div>
              <span className="font-bold text-gray-900 text-sm">SAI KRISHNA INDUSTRIES</span>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-gray-500">© 2026 Sai Krishna Industries. All Rights Reserved.</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Ludhiana Plant • Gurgaon NCR • Kolkata Depot | GSTIN: 03ACQFS9127A1Z6</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
